import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { Button } from '@/components/ui/Button';
import { useWebhookEndpointActions } from '@/features/routines/hooks/use-webhook-endpoint-actions';
import { useWebhookEndpointSetup } from '@/features/routines/hooks/use-webhook-endpoint-setup';
import { cn } from '@/lib/utils';

/**
 * The card for the setup link the agent writes after it creates an event source, where the user gives the endpoint
 * a secret. It renders inside a markdown paragraph, so it is built of spans.
 */
export const WebhookEndpointSetupCard = observer(
  function WebhookEndpointSetupCard({ endpointId }: { endpointId: string }) {
    const setup = useWebhookEndpointSetup(endpointId);
    const actions = useWebhookEndpointActions({
      context: 'WebhookEndpointSetupCard',
    });

    if (setup.status === 'loading') {
      return (
        <CardShell>
          <span className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
            <Icons.loader className="size-4 animate-spin" />
            Loading the event source…
          </span>
        </CardShell>
      );
    }

    if (setup.status === 'missing') {
      return (
        <CardShell>
          <span className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground">
            <Icons.info className="size-4 shrink-0" />
            Littlebird can&apos;t find this event source. It may have been
            deleted.
          </span>
        </CardShell>
      );
    }

    const { endpoint } = setup;
    return (
      <CardShell>
        <span className="flex items-center gap-3 px-4 pt-3">
          <span
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground',
              endpoint.hasSecret &&
                'rounded-full bg-green-500/10 text-green-500'
            )}
          >
            {endpoint.hasSecret ? (
              <Icons.check className="size-3.5" />
            ) : (
              <Icons.plugZap className="size-4" />
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-foreground">
              {endpoint.name}
            </span>
            <span className="block text-xs text-muted-foreground">
              {endpoint.hasSecret
                ? 'Secret set'
                : 'Needs a secret before it accepts events'}
            </span>
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-1 px-4 py-2">
          <code className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground">
            {endpoint.url}
          </code>
          <CopyButton
            text={endpoint.url}
            copyText="Copy webhook URL"
            size="sm"
            aria-label={`Copy URL for ${endpoint.name}`}
          />
        </span>
        {/* A service that signs with a secret it issues, such as Stripe, rejects a generated one, so the
            sender's secret can replace it */}
        {endpoint.canManage ? (
          <span className="flex flex-wrap justify-end gap-1 px-4 pb-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void actions.enterSenderSecret(endpoint)}
            >
              Use sender&apos;s secret
            </Button>
            {endpoint.hasSecret ? null : (
              <Button
                size="sm"
                onClick={() => void actions.regenerateSecret(endpoint.id)}
              >
                Generate secret
              </Button>
            )}
          </span>
        ) : (
          <span className="block pb-1" />
        )}
      </CardShell>
    );
  }
);

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <span className="my-3 block w-full max-w-md">
      <span className="block rounded-lg border border-border bg-background">
        {children}
      </span>
    </span>
  );
}
